import type {Middleware} from '@reduxjs/toolkit'
import Cookies from "js-cookie";
import {authenticate, logOut} from "@/lib/slices/authSlice";
import {setUserCurrency} from "@/lib/slices/cashierSlice";

const AUTH_COOKIE_OPTIONS = {
    expires: 30,
    sameSite: 'lax' as const,
    secure: process.env.NODE_ENV === 'production',
};

const USER_CURRENCY_COOKIE_OPTIONS = {expires: 30};

/**
 * AC-817: mirrors auth/currency state into cookies from *outside* the reducer.
 *
 * These writes used to live inside the `authenticate` / `logOut` /
 * `setUserCurrency` reducers. Impure reducers are exactly what Redux's
 * `isDispatching` guard exists to police, and the store was observed being
 * re-entered mid-dispatch (Redux #3/#6) on the landing route.
 *
 * The writes stay *synchronous* and happen before the action reaches the
 * reducer — exactly where they used to sit — so both store subscribers and
 * callers that read the cookie right after dispatching (e.g. the token refresh
 * in `lib/api/utils.ts`) observe the same ordering as before.
 */
export const cookieSyncMiddleware: Middleware = () => (next) => (action) => {
    if (authenticate.match(action)) {
        Cookies.set('accessToken', action.payload.accessToken, AUTH_COOKIE_OPTIONS);
        Cookies.set('refreshToken', action.payload.refreshToken, AUTH_COOKIE_OPTIONS);
        Cookies.set('deviceId', action.payload.deviceId, AUTH_COOKIE_OPTIONS);
    } else if (logOut.match(action)) {
        Cookies.remove('accessToken');
        Cookies.remove('refreshToken');
        Cookies.remove('deviceId');
    } else if (setUserCurrency.match(action)) {
        Cookies.set('user-currency', action.payload, USER_CURRENCY_COOKIE_OPTIONS);
    }

    return next(action);
};
